import { ACTION_STATUS, type MinutePerson } from '../actions/types'

/**
 * Toplantı Yönetimi ekranının tipleri (105) — `contracts/meeting-management-api.md` ile **birebir**.
 *
 * Alan adları sunucunun döndürdüğü adlardır; istemci yeniden adlandırmaz (bir yeniden adlandırma
 * katmanı, sözleşmedeki bir değişikliği sessizce yutup ekranı boş gösterebilirdi).
 */

/** Liste satırı — `MeetingManagementTaskDto`. */
export interface MeetingManagementTask {
  id: number
  /** Alt görevde dolu; satırın hangi ana görevin altında çizileceğini verir. */
  parentId: number | null
  /** `parentId != null`. Alt görev satırında seçim kutusu **çizilmez**. */
  subTask: boolean
  /**
   * Toplantıya bağlı olmayan **ana** görev — Ek kolonundaki "D" (Doğrudan Görev) / "T" (Toplantı
   * Bağımlı Görev) işaretinin **tek** ölçütü (`TaskOriginMark`). Sunucuda hesaplanır; ölçüt "Doğrudan
   * Görevler" süzgeç kutusununkiyle aynıdır. **Alt görev satırında daima `false`** ve orada işaret
   * hiç çizilmez — `false` alt görevde "toplantı bağımlı" anlamına GELMEZ; alt görev, işaretli ana
   * görevin hemen altında durur.
   *
   * **İsteğe bağlı (`?`), bilinçli:** THY ön ve arka ucu ayrı dağıtır; yeni ön uç bu alanı
   * göndermeyen eski arka uçla buluşabilir (T126'daki `nameFontSize` ile aynı durum). Alan yoksa
   * işaret HİÇ çizilmez — `undefined`'ı `false` saymak her doğrudan görevi "T" gösterirdi.
   */
  directTask?: boolean
  name: string
  description: string | null
  /** Ana Sorumlu (rol 340); alt görev satırında o görevin tek atananı. */
  mainResponsible: MinutePerson | null
  /**
   * Süreç Destek Yöneticileri (DB'de rol 341 "Yan Sorumlu") — **0..n**.
   *
   * **Alt görevde daima boştur**: alt görevde rol kavramı yoktur ve ekranda Ana/Yan ayrımı
   * gösterilmez, yalnız "Sorumlu" yazar (kullanıcı kuralı 2026-08-12). Sunucu boş liste döndürerek
   * bu kuralı yapısal olarak garanti eder; istemci ayrıca `subTask` kontrolü yapmak zorunda kalmaz.
   */
  secondaryResponsibles: MinutePerson[]
  /**
   * Görev tamamlanırken girilen not (`MEETING_MINUTES.COMPLETION_NOTE`). **Boş olması normaldir** —
   * not isteğe bağlıdır ve alan 2026-09-16 öncesinde hiçbir kayıtta doldurulmamıştı. Görevin
   * **notlarıyla** (kronolojik `MINUTES_NOTE` listesi) karıştırılmamalıdır.
   */
  completionNote: string | null
  /**
   * Görevin **en az bir aktif dosya eki** var mı. Satırdaki ataç işaretinin **tek** ölçütüdür; ataç
   * yoksa ek de yoktur.
   *
   * **Kapsam görevin TÜM ekleridir**, yalnız onaya gönderirken eklenenler değil: dosyalar tek havuzda
   * (`MEETING_MINUTES`) durur ve Görev Detay'ın Dokümanlar bölümünden eklenenle Onaya Gönderme Notu
   * pop-up'ından eklenen arasında saklanan bir fark yoktur.
   */
  hasAttachment: boolean
  statusId: number
  /** Durum **adı** — sunucuda dile göre çözülür; istemci sabit eşleme kurmaz. */
  statusName: string
  deadline: string | null
  /**
   * "Gecikti" bayrağı — satırın soft kırmızı gösterilmesinin **tek** ölçütü. İstemci
   * `deadline < bugün` hesabı **yapmaz**: aynı görev bu ekranda "geciken", Görev Detay'da
   * "gecikmemiş" görünemez.
   */
  delayedUpdated: boolean
  /** "Gecikme Gün Sayısı" — sunucuda hesaplanır; gecikme yoksa `null`. */
  lateDays: number | null
  /**
   * "Görev Süresi" (gün) — `güncel termin − verildiği gün − 1`, tabanı 0; verildiği gün kaydın
   * oluşturulma günüdür. Sunucuda hesaplanır; termin yoksa `null`.
   */
  taskDurationDays: number | null
  /** Sunucuda hesaplanır; istemci yeniden hesaplaMAZ. Alt görev satırında daima `false`. */
  closable: boolean
  /** Sunucuda hesaplanır; istemci yeniden hesaplaMAZ. Alt görev satırında daima `false`. */
  reopenable: boolean
  /** Optimistic lock damgası — kapatma/yeniden açma isteğinde **aynen geri gönderilir**. */
  modDate: string
  /**
   * Bu ana görevin alt görevleri; yalnız "Alt görevleri de getir" istendiğinde dolar. Alt görev
   * satırlarında daima boştur (tek kademe).
   *
   * **Neden iç içe, düz liste değil:** sayfa boyutu ana görev sayar. Alt görevler aynı diziye
   * karıştırılsaydı sayfa içeriği ilan edilen boyutu aşar ve sunucunun sayfa modeli "toplam kayıt"
   * sayısını içerik boyutuna göre yukarı düzeltirdi — kutu işaretlenince kullanıcı toplam sayının
   * sıçradığını görürdü. Tabloyu çizen taraf satırları düzleştirir ({@link flattenTasks}).
   */
  subTasks: MeetingManagementTask[]
}

/**
 * İç içe gelen sayfayı tabloya verilecek **düz** satır dizisine çevirir: her ana görev, ardından
 * kendi alt görevleri. Sunucudan gelen düzen korunur; istemci yeniden sıralamaz.
 */
export function flattenTasks(tasks: MeetingManagementTask[]): MeetingManagementTask[] {
  return tasks.flatMap((task) => [task, ...task.subTasks])
}

/** "Gecikme Gün Sayısı" metni: `-3` ya da "Gecikme Bulunmamaktadır". */
export function lateDaysText(lateDays: number | null, t: (key: string) => string): string {
  return lateDays == null ? t('meetingManagement.lateDays.none') : `-${lateDays}`
}

/**
 * "Görev Süresi" metni: "4 gün". Termin yoksa `null` — boş-değer gösterimi görünümün kendi
 * geleneğidir (tabloda boş hücre, kartta tire).
 */
export function taskDurationText(
  days: number | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  return days == null ? null : t('meetingManagement.taskDuration.value', { count: days })
}

/** "Görevi Oluşturan" süzgecinin seçeneği — sicil değil **kullanıcı** kimliği taşır. */
export interface TaskCreatorOption {
  userId: number
  displayName: string
}

/** "Toplantı Grubu" süzgecinin seçeneği — `GET /meeting-groups`. */
export interface MeetingGroupOption {
  id: number
  name: string
}

/** Tablonun görünüm ayarları — `GET /display-settings`; yazı boyları piksel. */
export interface MeetingManagementDisplaySettings {
  completionNoteFontSize: number
  descriptionFontSize: number
  nameFontSize: number
}

/** Kişi süzgeçlerinin rolü. İki liste **ayrıdır** ve birbirinin yerine geçmez. */
export type ResponsibleRole = 'main' | 'support'

/** Liste sorgusunun süzgeçleri + sayfalama. */
export interface MeetingManagementQuery {
  /** Bu durumların tümü. Üç durum listesi de boş → arşivli hariç tüm durumlar. */
  statusIds?: number[]
  /** Bu durumların yalnız gecikenleri (gecikme bayrağı Evet). */
  delayedStatusIds?: number[]
  /** Bu durumların yalnız gecikmeyenleri (bayrak Hayır ya da boş). */
  onTimeStatusIds?: number[]
  creatorUserId?: number | null
  mainResponsibleEmployeeId?: string | null
  supportManagerEmployeeId?: string | null
  /**
   * Görevin toplantısının grubu bunlardan biri (VEYA). Boş → grup süzgeci yok. Yalnız **toplantıya
   * bağlı** görevleri daraltır; doğrudan görevleri {@link includeDirectTasks} yönetir.
   */
  meetingGroupIds?: number[]
  /**
   * "Doğrudan Görevler" — toplantısız ana görevler listeye girsin mi. Grup seçiliyken de geçerlidir:
   * işaretliyse seçili grupların görevlerine **eklenirler**. Gönderilmezse sunucu `true` sayar.
   */
  includeDirectTasks?: boolean
  /** `true` → sayfadaki ana görevlerin alt görevleri de döner. Toplam kayıt sayısını **değiştirmez**. */
  includeSubTasks?: boolean
  page?: number
  /** **Ana görev** sayar; alt görevler sayfa boyutuna girmez. */
  size?: number
  /** Spring sort: `alan,asc|desc`. */
  sort?: string
}

/** Toplu kapatma isteğinin bir öğesi. */
export interface CloseItem {
  id: number
  /** Listede dönen `modDate`, aynen. */
  modDate: string
}

/** Yeniden açma isteği — görev **başına** bir istek. */
export interface ReopenRequest {
  /** ISO `yyyy-MM-dd`. Zorunlu; bugün veya sonrası. */
  newDeadline: string
  /** İsteğe bağlı; en çok 2.000 karakter. Boş/yalnız boşluk → hiç not kaydı oluşmaz. */
  note: string | null
  modDate: string
}

/**
 * Durum süzgecinde **gecikmeye göre ikiye bölünen** durumlar: her biri "… - Geciken" ve
 * "… - Gecikmeyen" olarak iki seçenek verir (30.09.2026 kullanıcı isteği). Ölçüt satırın kırmızı
 * boyanmasıyla aynı alandır (`delayedUpdated`); istemci tarih hesabı yapmaz.
 */
export const DELAY_SPLIT_STATUS_IDS: number[] = [ACTION_STATUS.PENDING, ACTION_STATUS.COMPLETED]

/** Bölünen bir durumun hangi yarısı. */
export type DelayPart = 'delayed' | 'onTime'

/**
 * Durum süzgecinin seçenek değeri: bölünmemiş durumda yalnız kimlik (`"333"`), bölünende kimlik +
 * yarı (`"330:delayed"`). MultiSelect skaler değer ister; bu yüzden tek bir dizgeye kodlanır.
 */
export function statusKey(id: number, part?: DelayPart): string {
  return part ? `${id}:${part}` : String(id)
}

/** Seçili süzgeç değerleri → sunucunun üç durum listesi (aralarında VEYA vardır). */
export function statusQueryOf(
  keys: string[],
): Pick<MeetingManagementQuery, 'statusIds' | 'delayedStatusIds' | 'onTimeStatusIds'> {
  const statusIds: number[] = []
  const delayedStatusIds: number[] = []
  const onTimeStatusIds: number[] = []
  for (const key of keys) {
    const [id, part] = key.split(':')
    if (part === 'delayed') delayedStatusIds.push(Number(id))
    else if (part === 'onTime') onTimeStatusIds.push(Number(id))
    else statusIds.push(Number(id))
  }
  return { statusIds, delayedStatusIds, onTimeStatusIds }
}

/**
 * Ekranın varsayılan durum süzgeci: **Aksiyon Bekleniyor - Geciken + Onay Bekleniyor (ikisi de)** —
 * bölünmeden önceki açılış listesinin aynısı.
 */
export const DEFAULT_STATUS_KEYS: string[] = [
  statusKey(ACTION_STATUS.PENDING, 'delayed'),
  statusKey(ACTION_STATUS.COMPLETED, 'delayed'),
  statusKey(ACTION_STATUS.COMPLETED, 'onTime'),
]

/**
 * Durum süzgecinin seçenek kümesi. `339 Arşivlendi` yoktur — arşiv ayrı bir ekrandır, bu listede hiç
 * görünmez.
 *
 * **22.09.2026'dan beri alt görev de aynı statüleri taşır** (332 Devam Ediyor kalktı, Kapatıldı
 * eklendi), yani küme artık iki kademe için de geçerlidir. DB'de kalan eski 332 kayıtları satırda
 * görünebilir ama süzgeçte seçilemez: kataloğdan kalkmış bir statüye süzgeç açılmaz.
 */
export const FILTERABLE_STATUS_IDS: number[] = [
  ACTION_STATUS.PENDING,
  ACTION_STATUS.COMPLETED,
  ACTION_STATUS.CLOSED,
]

/** Not alanlarının ürün sınırı (tamamlanma notu ve yeniden açma notu için **aynı**). */
export const NOTE_MAX_LENGTH = 2000

/**
 * Tek istekte kapatılabilecek en fazla görev sayısı — sunucudaki
 * `MeetingManagementCloseRequest.MAX_ITEMS` ile **aynı** olmak zorundadır.
 *
 * <p>Ulaşılabilir bir sınırdır (bağımsız code-review bulgusu 2026-09-16): sayfa boyutu seçenekleri
 * **200'e kadar** çıkıyor ve seçim sayfa değişiminde temizlenmiyor, yani kullanıcı iki sayfadan
 * toplam 201 görev işaretleyebilir. Sunucu o isteği `400` ile reddeder ve ekran kendi hata→mesaj
 * eşlemesini tutmadığı için kullanıcı yalnız merkezî genel hatayı görürdü — sebebi ekranda
 * görünmezdi. Bu yüzden sınır **gönderimden önce** burada da uygulanır.
 */
export const CLOSE_MAX_ITEMS = 200
